/**
 * Event creation/edit modal.
 * Supports title, start/end time, all-day toggle, color, description.
 */

import { useState, useEffect, type FormEvent } from 'react';
import type { Event, CreateEventInput } from '@anna-calendar/shared';
import { DEFAULT_EVENT_COLOR } from '@anna-calendar/shared';
import { Modal } from './Modal';
import { formatDateISO } from '../utils/dates';

interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateEventInput) => void;
  event?: Event | null;
  defaultDate?: string;
}

const COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f97316',
  '#f59e0b', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6',
];

const REMINDER_OPTIONS = [
  { value: 0, label: 'No reminder' },
  { value: 5, label: '5 minutes before' },
  { value: 10, label: '10 minutes before' },
  { value: 15, label: '15 minutes before' },
  { value: 30, label: '30 minutes before' },
  { value: 60, label: '1 hour before' },
];

export function EventModal({ isOpen, onClose, onSave, event, defaultDate }: EventModalProps) {
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [color, setColor] = useState(DEFAULT_EVENT_COLOR);
  const [description, setDescription] = useState('');
  const [reminderMinutes, setReminderMinutes] = useState(0);

  useEffect(() => {
    if (event) {
      setTitle(event.title);
      const d = new Date(event.start_time);
      setStartDate(formatDateISO(d));
      setStartTime(event.all_day ? '' : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`);
      if (event.end_time) {
        const ed = new Date(event.end_time);
        setEndTime(`${String(ed.getHours()).padStart(2, '0')}:${String(ed.getMinutes()).padStart(2, '0')}`);
      } else {
        setEndTime('');
      }
      setAllDay(event.all_day === 1);
      setColor(event.color);
      setDescription(event.description || '');
      setReminderMinutes(event.reminder_minutes ?? 0);
    } else {
      setTitle('');
      setStartDate(defaultDate || formatDateISO(new Date()));
      setStartTime('');
      setEndTime('');
      setAllDay(false);
      setColor(DEFAULT_EVENT_COLOR);
      setDescription('');
      setReminderMinutes(0);
    }
  }, [event, isOpen, defaultDate]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !startDate) return;

    const startISO = allDay
      ? `${startDate}T00:00:00`
      : `${startDate}T${startTime || '00:00'}:00`;
    const endISO = !allDay && endTime
      ? `${startDate}T${endTime}:00`
      : undefined;

    onSave({
      title: title.trim(),
      description: description.trim() || null,
      start_time: startISO,
      end_time: endISO || null,
      all_day: allDay ? 1 : 0,
      color,
      recurrence_rule: null,
      reminder_minutes: reminderMinutes > 0 ? reminderMinutes : null,
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={event ? 'Edit Event' : 'New Event'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Event title"
            required
            className="w-full px-3 py-3 rounded-lg text-base outline-none"
            style={{
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
              minHeight: '44px',
            }}
          />
        </div>

        {/* Date */}
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            Date
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
            className="w-full px-3 py-3 rounded-lg text-base outline-none"
            style={{
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
              minHeight: '44px',
            }}
          />
        </div>

        {/* All-day toggle */}
        <label className="flex items-center gap-3 cursor-pointer">
          <div
            className="relative w-11 h-6 rounded-full transition-colors"
            style={{ backgroundColor: allDay ? 'var(--accent)' : 'var(--bg-tertiary)' }}
            onClick={() => setAllDay(!allDay)}
          >
            <div
              className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full transition-transform"
              style={{
                backgroundColor: '#fff',
                transform: allDay ? 'translateX(20px)' : 'translateX(0)',
              }}
            />
          </div>
          <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>All day</span>
        </label>

        {/* Time pickers */}
        {!allDay && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                Start time
              </label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-3 py-3 rounded-lg text-base outline-none"
                style={{
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  minHeight: '44px',
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                End time
              </label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-3 py-3 rounded-lg text-base outline-none"
                style={{
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  minHeight: '44px',
                }}
              />
            </div>
          </div>
        )}

        {/* Color picker */}
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
            Color
          </label>
          <div className="flex flex-wrap gap-2">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                className="w-9 h-9 rounded-full transition-transform"
                style={{
                  backgroundColor: c,
                  transform: color === c ? 'scale(1.2)' : 'scale(1)',
                  border: color === c ? '2px solid var(--text-primary)' : '2px solid transparent',
                }}
                aria-label={`Color ${c}`}
              />
            ))}
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
            rows={3}
            className="w-full px-3 py-3 rounded-lg text-base outline-none resize-none"
            style={{
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
            }}
          />
        </div>

        {/* Reminder */}
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            Reminder
          </label>
          <select
            value={reminderMinutes}
            onChange={(e) => setReminderMinutes(Number(e.target.value))}
            className="w-full px-3 py-3 rounded-lg text-base outline-none"
            style={{
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
              minHeight: '44px',
            }}
          >
            {REMINDER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={!title.trim() || !startDate}
          className="w-full py-3 rounded-lg text-base font-semibold transition-opacity"
          style={{
            backgroundColor: 'var(--accent)',
            color: '#fff',
            opacity: !title.trim() || !startDate ? 0.5 : 1,
            minHeight: '44px',
          }}
        >
          {event ? 'Save Changes' : 'Create Event'}
        </button>
      </form>
    </Modal>
  );
}
